import { useEffect, useState, useCallback } from 'react'
import { Plus, X, ChevronDown, ChevronRight, RefreshCw, Send } from 'lucide-react'
import toast from 'react-hot-toast'
import { supportTicketService, type AuthTicketCreate } from '../services/supportTicketService'
import type { SupportTicket, TicketStatus, TicketCategory, TicketPriority } from '../types/api.types'

function statusColor(s: TicketStatus): string {
  const map: Record<TicketStatus, string> = {
    OPEN:        'bg-amber-100 text-amber-800',
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    RESOLVED:    'bg-green-100 text-green-800',
    CLOSED:      'bg-gray-100 text-gray-500',
  }
  return map[s] ?? 'bg-gray-100 text-gray-400'
}

function priorityColor(p: TicketPriority): string {
  const map: Record<TicketPriority, string> = {
    CRITICAL: 'bg-red-100 text-red-800',
    HIGH:     'bg-orange-100 text-orange-800',
    MEDIUM:   'bg-amber-100 text-amber-800',
    LOW:      'bg-green-100 text-green-800',
  }
  return map[p] ?? 'bg-gray-100 text-gray-400'
}

function fmtDate(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

const CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: 'TECHNICAL', label: 'Technical Issue' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'FEATURE_REQUEST', label: 'Feature Request' },
  { value: 'OTHER', label: 'Other' },
]

const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
]

interface NewTicketModalProps {
  onClose: () => void
  onCreated: (t: SupportTicket) => void
}

function NewTicketModal({ onClose, onCreated }: NewTicketModalProps): JSX.Element {
  const [form, setForm] = useState<AuthTicketCreate>({
    category: 'TECHNICAL',
    priority: 'MEDIUM',
    subject: '',
    description: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function set(key: keyof AuthTicketCreate, value: string) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true); setError('')
    try {
      const ticket = await supportTicketService.submitAuthenticated(form)
      toast.success(`Ticket ${ticket.ticket_number} submitted. We'll get back to you shortly.`)
      onCreated(ticket)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit ticket')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-[16px] font-bold text-gray-900">Submit Support Ticket</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Category *</label>
              <select
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Priority *</label>
              <select
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">Subject *</label>
            <input
              required
              value={form.subject}
              onChange={(e) => set('subject', e.target.value)}
              placeholder="Brief summary of the issue"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
            />
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-gray-700">Description *</label>
            <textarea
              required
              rows={5}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Describe the issue in detail — include error messages, steps to reproduce, and when it started..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
            />
          </div>

          {error && <p className="text-[12px] text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black hover:opacity-80 disabled:opacity-50"
            >
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TicketRow({ ticket }: { ticket: SupportTicket }): JSX.Element {
  const [open, setOpen] = useState(false)

  return (
    <>
      <tr
        className="cursor-pointer border-t border-gray-100 hover:bg-gray-50/60"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <span className="font-mono text-[12px] text-gray-500">{ticket.ticket_number}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <p className="text-[13px] font-medium text-gray-900">{ticket.subject}</p>
          <p className="text-[11px] text-gray-400">{ticket.category.replace('_', ' ')}</p>
        </td>
        <td className="px-4 py-3">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColor(ticket.status)}`}>
            {ticket.status.replace('_', ' ')}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${priorityColor(ticket.priority)}`}>
            {ticket.priority}
          </span>
        </td>
        <td className="px-4 py-3 text-[12px] text-gray-500">{fmtDate(ticket.created_at)}</td>
        <td className="px-4 py-3 text-center text-[12px] text-gray-500">{ticket.replies.filter((r) => !r.is_internal).length}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="bg-gray-50/40 px-4 py-4">
            <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
              <div>
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">Description</p>
                <p className="whitespace-pre-wrap text-[13px] text-gray-700">{ticket.description}</p>
              </div>

              {ticket.resolution_notes && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-[13px] text-green-800">
                  <p className="mb-0.5 text-[11px] font-medium uppercase tracking-wide text-green-600">Resolution</p>
                  {ticket.resolution_notes}
                </div>
              )}

              {ticket.replies.filter((r) => !r.is_internal).length > 0 && (
                <div>
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">Replies</p>
                  <div className="space-y-2">
                    {ticket.replies
                      .filter((r) => !r.is_internal)
                      .map((r) => (
                        <div key={r.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                          <div className="mb-1 flex items-center gap-2 text-[11px] text-gray-500">
                            <span className="font-medium text-gray-700">{r.replied_by_name}</span>
                            <span>{fmtDate(r.created_at)}</span>
                          </div>
                          <p className="whitespace-pre-wrap text-[13px] text-gray-700">{r.message}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

const STATUS_FILTERS: { value: '' | TicketStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
]

export function TenantSupportPage(): JSX.Element {
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'' | TicketStatus>('')
  const [showNew, setShowNew] = useState(false)

  const load = useCallback(async (status?: '' | TicketStatus) => {
    setLoading(true)
    try {
      const res = await supportTicketService.listMine(status || undefined)
      setTickets(res.tickets)
    } catch {
      setTickets([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(statusFilter) }, [statusFilter, load])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Support Tickets</h1>
          <p className="mt-1 text-sm text-gray-500">View and manage your support requests</p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 rounded-lg bg-brand-green px-3 py-2 text-[13px] font-semibold text-black hover:opacity-80"
        >
          <Plus size={14} />
          New Ticket
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-gray-200">
        {STATUS_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatusFilter(value)}
            className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-[13px] transition-colors sm:px-4 ${
              statusFilter === value
                ? 'border-brand-green font-semibold text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(statusFilter)}
          disabled={loading}
          className="ml-auto flex shrink-0 items-center gap-1 rounded px-2 py-1 text-[12px] text-gray-400 hover:text-gray-600 disabled:opacity-40"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Tickets table */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {loading ? (
          <p className="py-12 text-center text-[13px] text-gray-400">Loading...</p>
        ) : tickets.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-[14px] text-gray-500">No tickets yet.</p>
            <p className="mt-1 text-[12px] text-gray-400">Click "New Ticket" to get help from our support team.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Ticket #</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Subject</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Status</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Priority</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Submitted</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Replies</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => <TicketRow key={t.id} ticket={t} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && (
        <NewTicketModal
          onClose={() => setShowNew(false)}
          onCreated={(t) => { setTickets((prev) => [t, ...prev]); setShowNew(false) }}
        />
      )}
    </div>
  )
}
